import * as React from 'react';
import { DayPicker } from 'react-day-picker';
import { cn } from '../../lib/utils';
import { buttonVariants } from './button';

type CalendarProps = React.ComponentProps<typeof DayPicker>;

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout,
  navLayout,
  ...props
}: CalendarProps) {
  const isDropdownLayout = captionLayout?.startsWith('dropdown') ?? false;
  const resolvedNavLayout = navLayout ?? undefined;
  const navButtonClass = cn(
    buttonVariants({ variant: 'ghost' }),
    'h-9 w-9 bg-transparent p-0 text-text-secondary transition-all duration-150 hover:bg-neutral-100 hover:text-text-primary'
  );

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      captionLayout={captionLayout}
      navLayout={resolvedNavLayout}
      className={cn('p-4', className)}
      classNames={{
        months: cn('rdp-months', 'relative flex flex-col gap-5 sm:flex-row sm:gap-6'),
        month: cn('rdp-month', 'space-y-5'),
        month_caption: cn(
          'rdp-month_caption',
          'relative flex h-9 items-center justify-center gap-2 px-10'
        ),
        caption_label: cn(
          'rdp-caption_label',
          'text-sm font-semibold text-text-primary',
          // Hide caption label when using dropdown mode (dropdowns show month/year instead)
          isDropdownLayout && 'hidden'
        ),
        nav: cn('rdp-nav', 'absolute inset-x-0 top-0 flex items-center justify-between gap-1'),
        button_previous: cn('rdp-button_previous', navButtonClass),
        button_next: cn('rdp-button_next', navButtonClass),
        month_grid: cn('rdp-month_grid', 'w-full border-collapse'),
        weekdays: cn(
          'rdp-weekdays',
          'grid grid-cols-7 gap-2 text-[11px] font-semibold uppercase tracking-wide text-text-secondary'
        ),
        weekday: cn(
          'rdp-weekday',
          'flex h-9 items-center justify-center rounded-sm text-text-secondary'
        ),
        week: cn('rdp-week', 'grid w-full grid-cols-7 gap-2 mt-2'),
        day: cn(
          'rdp-day',
          'relative flex h-full w-full aspect-square items-center justify-center p-0 text-text-primary'
        ),
        day_button: cn(
          'rdp-day_button',
          'flex h-full w-full cursor-pointer select-none items-center justify-center rounded-md text-sm font-medium transition-all duration-150',
          'hover:bg-neutral-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue-500/30 focus-visible:ring-offset-2'
        ),
        selected:
          'rdp-selected bg-brand-blue-600 text-white shadow-sm hover:bg-brand-blue-700 focus-visible:ring-brand-blue-300 focus-visible:ring-offset-2',
        range_start:
          'rdp-range_start bg-brand-blue-600 text-white shadow-sm hover:bg-brand-blue-700',
        range_end: 'rdp-range_end bg-brand-blue-600 text-white shadow-sm hover:bg-brand-blue-700',
        range_middle: 'rdp-range_middle bg-brand-blue-100 text-text-primary',
        today: 'rdp-today border border-dashed border-brand-blue-600 text-brand-blue-700',
        outside: 'rdp-outside text-text-tertiary opacity-40 hover:bg-transparent',
        disabled: 'rdp-disabled text-text-tertiary opacity-30 line-through hover:bg-transparent',
        hidden: 'rdp-hidden invisible',
        // Dropdown styles for captionLayout="dropdown"
        dropdown:
          'rdp-dropdown h-9 rounded-md border border-[var(--input-border-default)] bg-surface px-2 text-sm font-medium text-text-primary transition-colors hover:border-[var(--input-border-hover)] focus:outline-none focus:ring-2 focus:ring-brand-blue-500/30',
        dropdowns: 'rdp-dropdowns flex items-center gap-2',
        chevron: 'rdp-chevron fill-text-secondary transition-transform duration-150',
        ...classNames,
      }}
      {...props}
    />
  );
}
Calendar.displayName = 'Calendar';

export { Calendar };
